import * as React from 'react'
import { CreateInvoiceDetail } from '../InvoiceDetailStore'
import { notification, message } from 'antd'
import { observer } from 'mobx-react-lite'
import { inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { API, IInvoice, TaxGroup } from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { browserHistory } from 'react-router'
import { Routes } from '../../../../../constants/Routes'

interface Props {
  title: string | JSX.Element
  widthBreakPoint?: number
  createInvoice: CreateInvoiceDetail
  getInvoiceTaxGroup: (invoiceDetail: IInvoice) => TaxGroup | undefined
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

const notificationConfig = {
  duration: 0,
  top: 55,
}

const createCustomError = (customErrorMessage: string) => {
  return notification.error({
    message: 'Error',
    description: customErrorMessage,
    ...notificationConfig,
  })
}

export const SaveInvoiceDetailButton = inject('currentCompanyStore')(
  observer((props: Props & InjectedProps) => {
    const { title, createInvoice, getInvoiceTaxGroup, currentCompanyStore } = props
    const companyId = currentCompanyStore!.company!.id
    const invoiceNetworkObject = createInvoice.getNetworkObject()

    const [isSaving, setIsSaving] = React.useState(false)

    const getDuplicateProductNames = (): string[] => {
      /**
       * lookup is an object consisting of the number of duplicate counts & corresponding product id
       * Example
       * lookup = {
       *    'product-id-1': 2,
       *    'product-id-2' : 1
       * }
       */
      const lookup = createInvoice.lines.reduce((prev, curr) => {
        const key = curr.productId || 'null'

        prev[key] = ++prev[key] || 0
        return prev
      }, {})

      const duplicates = createInvoice.lines.filter(
        (line) => line.productId && lookup[line.productId],
      )

      const duplicateLineNames: string[] = []

      duplicates.forEach((duplicate) => {
        if (!duplicateLineNames.includes(duplicate.productName)) {
          duplicateLineNames.push(duplicate.productName)
        }
      })

      return duplicateLineNames
    }

    const validateInvoice = (): boolean => {
      if (!createInvoice.title) {
        notification.error({
          message: 'Title required',
          description: 'Please add a title and try again.',
          ...notificationConfig,
        })

        return false
      }

      if (!createInvoice.locationId) {
        notification.error({
          message: 'Location required',
          description: 'Please select a location and try again.',
          ...notificationConfig,
        })

        return false
      }

      if (createInvoice.lines.length === 0) {
        notification.error({
          message: 'Products required',
          description: 'Please add at least 1 product line.',
          ...notificationConfig,
        })

        return false
      }

      if (createInvoice.fees.find((fee) => !fee.name)) {
        notification.error({
          message: 'Fee name required',
          description: 'Please add a fee name',
          ...notificationConfig,
        })

        return false
      }

      if (createInvoice.linesWithoutMatchingBatchSum.length > 0) {
        notification.error({
          message: 'Unmatching lot quantities',
          description: (
            <div>
              <p>
                The following products' quantities do not match the sums of their lot quantities
              </p>
              <ul>
                {createInvoice.linesWithoutMatchingBatchSum.map((line, index) => (
                  <li key={index}>{line.productName}</li>
                ))}
              </ul>
            </div>
          ),
          ...notificationConfig,
        })

        return false
      }

      const duplicateProductNames = getDuplicateProductNames()

      if (duplicateProductNames.length > 0) {
        notification.error({
          message: 'Duplicate products',
          description: (
            <div>
              <p>There are duplicate products:</p>
              <ul>
                {duplicateProductNames.map((productName, index) => (
                  <li key={index}>{productName}</li>
                ))}
              </ul>
            </div>
          ),
          ...notificationConfig,
        })

        return false
      }

      return true
    }

    const onSubmission = (invoiceDetail: IInvoice) => {
      createInvoice.setInvoiceDetail(invoiceDetail)
      createInvoice.populate(invoiceDetail)

      const matchingTaxGroup = getInvoiceTaxGroup(invoiceDetail)

      if (matchingTaxGroup) {
        createInvoice.setTaxGroup(matchingTaxGroup)
      }

      createInvoice.setIsEditMode(false)
      setIsSaving(false)

      browserHistory.push(Routes.EDIT_INVOICE(companyId, invoiceDetail.id))

      // Reset `isEdited` and `isSaved` after saving the PO
      createInvoice.setIsEdited(false)
      createInvoice.setIsSaved(false)
    }

    const editInvoice = async (invoiceId: string) => {
      // Editing
      try {
        const updatedInvoice = await API.updateInvoice(companyId, invoiceId, invoiceNetworkObject)
        await onSubmission(updatedInvoice)
        message.success('Purchase order successfully edited')
      } catch (error) {
        createCustomError(parseErrorMsg(error) || 'There was an error updating your purchase order')
      }
    }

    const addInvoice = async () => {
      // Adding new invoice
      try {
        const newInvoice = await API.createInvoice(companyId, invoiceNetworkObject)
        await onSubmission(newInvoice)
        message.success('Purchase order successfully saved')
      } catch (error) {
        createCustomError(parseErrorMsg(error) || 'There was an error creating your purchase order')
      }
    }

    const saveInvoiceDetail = async () => {
      const isValidated = validateInvoice()

      if (!isValidated) {
        return
      }

      setIsSaving(true)

      if (createInvoice.invoiceDetail) {
        await editInvoice(createInvoice.invoiceDetail.id)
      } else {
        await addInvoice()
      }
    }

    return (
      <BlazeButton
        buttonProps={{
          type: 'primary',
          disabled: isSaving,
          onClick: saveInvoiceDetail,
          loading: isSaving,
          style: { width: 200 },
        }}
        titleProps={{
          title,
        }}
      />
    )
  }),
)
